import { useState } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageUpload = ({ 
  value, 
  onChange, 
  folder = "general",
  label = "Image",
  placeholder = "Enter image URL or upload",
  showPreview = true,
  previewHeight = "h-32"
}) => {
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    setImageError(false);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", folder);

    try {
      const response = await axios.post(`${API}/admin/upload/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onChange(response.data.url);
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    onChange("");
    setImageError(false);
  };

  const getFullUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (url.startsWith("/uploads")) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  // Reset error state when value changes
  const handleValueChange = (newValue) => {
    setImageError(false);
    onChange(newValue);
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-[#A1A1AA] text-sm font-medium">{label}</label>
      )}
      
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={placeholder}
            className="w-full bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] p-3 pr-10 focus:border-[#D4AF37] focus:outline-none text-sm"
          />
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#A1A1AA] hover:text-red-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <label className={`px-4 py-3 border border-[rgba(212,175,55,0.3)] text-[#D4AF37] hover:bg-[#D4AF37]/10 cursor-pointer flex items-center gap-2 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload className="w-4 h-4" />
          <span className="text-sm">{uploading ? "..." : "Upload"}</span>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
        </label>
      </div>

      {showPreview && (
        <div className={`${previewHeight} bg-[#0A0A0A] border border-[rgba(212,175,55,0.1)] overflow-hidden relative`}>
          {value && !imageError ? (
            <img
              src={getFullUrl(value)}
              alt="Preview"
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#52525B]">
              <div className="text-center">
                <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <span className="text-xs">{imageError ? "Failed to load image" : "No image selected"}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
